import { handleActions } from '@gef-ui/utils/fsaActions';
import invariant from 'invariant';
import path from "ramda/es/path";
import has from "ramda/es/has";
import hasPath from "ramda/es/hasPath";
import includes from "ramda/es/includes";
import isNil from "ramda/es/isNil";
import pathEq from "ramda/es/pathEq";
import isArray from "ramda-extension/es/isArray";
import { useLayoutEffect, useReducer, useRef } from 'react';
import { asyncScheduler, pipe } from 'rxjs';
import { filter, observeOn, partition } from 'rxjs/operators';
import { affectedFieldsTypes } from '../../../stream/constants';
import { useStreamContext } from '../../../stream/context';
import { actionNames, validationRunner } from '../../../stream/transfer/transmission';
import { enhanceWithResolvedFieldDescriptor } from '../../../stream/utils';
import { createFieldRecord, metaAsync } from '../../../utils/data';
import { hasAccessibleValidation, hasVisibileValidation, rejectSpecialValidations } from '../../../validations/utils';
const shouldSkipUpdate = (meta, storedRunner)=>meta.runner !== validationRunner.ALL && storedRunner !== validationRunner.ALL && meta.runner !== storedRunner;
const takeFirstValidation = (state, { payload, meta })=>{
    const firstValidation = rejectSpecialValidations(payload[state.fieldRecord])[0];
    if (isNil(firstValidation) && shouldSkipUpdate(meta, state.validationRunner)) {
        return state;
    }
    return {
        ...state,
        validationRunner: firstValidation ? meta.runner : undefined,
        validationType: firstValidation?.type,
        errorMessage: firstValidation?.errorMessage,
        errMeta: firstValidation?.metaInfo
    };
};
const resolveSpecialValidations = (state, { payload, meta })=>{
    let newState = {
        accessibleByValidation: state.accessibleByValidation,
        visibleByValidation: state.visibleByValidation,
        accessibleValidationRunner: state.accessibleValidationRunner,
        visibleValidationRunner: state.visibleValidationRunner
    };
    const brokenAccessible = hasAccessibleValidation(payload[state.fieldRecord]);
    if (brokenAccessible || !shouldSkipUpdate(meta, state.accessibleValidationRunner)) {
        newState = {
            ...newState,
            accessibleByValidation: !brokenAccessible,
            accessibleValidationRunner: meta.runner
        };
    }
    const brokenVisible = hasVisibileValidation(payload[state.fieldRecord]);
    if (brokenVisible || !shouldSkipUpdate(meta, state.visibleValidationRunner)) {
        newState = {
            ...newState,
            visibleByValidation: !brokenVisible,
            visibleValidationRunner: meta.runner
        };
    }
    return newState;
};
const updateAsyncValidationProgressState = (state, { payload })=>({
        ...state,
        asyncValidationInProgress: payload[state.fieldRecord]?.value
    });
const reducer = (reducerHandler = {})=>handleActions({
        [actionNames.UPDATE_FORM_DATA]: (state, { payload, meta })=>{
            if (pathEq(state.value, meta.fieldDescriptor.deepPath, payload)) {
                return state;
            }
            return {
                ...state,
                value: path(meta.fieldDescriptor.deepPath, payload)
            };
        },
        [actionNames.UPDATE_VALIDATION_RESULT]: (state, action)=>({
                ...takeFirstValidation(state, action),
                ...resolveSpecialValidations(state, action)
            }),
        [actionNames.UPDATE_ASYNC_VALIDATION_STATE]: updateAsyncValidationProgressState,
        INIT: (_state, action)=>action.payload,
        ...reducerHandler
    }, {});
// tahle funkce muze potencialne brzdit update pokud bude slozita. slo by potencialne rozdelit po funkcionalite
// ne vzdy je nutny ten descriptor, nekdy lze pridat az potom. apod.
// zatim to ale vypada ze je rychla dostatecne
const filterActionsForFieldAndAddDescriptor = (formDescriptor$, fieldName, fieldIndices, fieldRecord)=>pipe(enhanceWithResolvedFieldDescriptor(formDescriptor$, {
        fieldName,
        fieldIndices
    }), filter(({ meta: { affectedFields, fieldDescriptor } = {}, payload })=>{
        invariant(affectedFields, 'transmission action does not have affected fields');
        if (affectedFields === affectedFieldsTypes.ALL) {
            return true;
        } else if (affectedFields === affectedFieldsTypes.BY_DEEP_PATH) {
            return hasPath(fieldDescriptor.deepPath, payload);
        } else if (affectedFields === affectedFieldsTypes.BY_RECORD) {
            return has(fieldRecord, payload);
        }
        return isArray(affectedFields) && includes(fieldRecord, affectedFields);
    }));
/**
 * Provides field state for transmission stream
 *
 * @documented
 * @category Field
 * @import import useFieldTransmission from '@gef-ui/form-core/hooks/field/useFieldTransmission'
 * @param {Object} input
 * @param {String} input.fieldName
 * @param {Number[]} [input.fieldIndices] - indices for array, can be nested and sorted by path of nesting
 * @param {Object} [options]
 * @param {Object} [options.reducerHandler] - custom handler for additional actions. See source code :)
 * @returns {Object} state - whole state for field
 * @returns {*} state.value - value of the field
 * @returns {String} [state.validationType] - broken validation type
 * @returns {String} [state.errorMessage] - broken error message
 * @example
 *
 * 	const {value, validationType, errorMessage} = useFieldTransmission({fieldName: 'xxx'});
 *
 */ const useFieldTransmission = ({ fieldName, fieldIndices }, transmissionExtension)=>{
    const fieldRecord = createFieldRecord(fieldName, fieldIndices);
    const { transmission$, formDescriptor$, streamExtension } = useStreamContext();
    const { reducerHandler, createInitState } = streamExtension?.fieldTransmissionExtension || transmissionExtension || {};
    const firstTimeInit = useRef(false);
    const [state, dispatch] = useReducer(reducer(reducerHandler), {
        fieldName,
        fieldIndices,
        asyncValidationInProgress: false,
        accessibleByValidation: true,
        visibleByValidation: true,
        fieldRecord
    }, createInitState);
    useLayoutEffect(()=>{
        if (firstTimeInit.current) {
            dispatch({
                type: 'INIT',
                payload: {
                    fieldName,
                    fieldIndices,
                    asyncValidationInProgress: false,
                    accessibleByValidation: true,
                    visibleByValidation: true,
                    fieldRecord
                }
            });
        } else {
            firstTimeInit.current = true;
        }
    }, [
        fieldRecord,
        fieldName,
        fieldIndices
    ]);
    // tohle je synchronni update, vypada to trochu lepe a neblika to. ale zase nevim jestli to tak chceme. blokuje to vizual.
    useLayoutEffect(()=>{
        const [asyncUpdate, syncUpdate] = transmission$.pipe(filterActionsForFieldAndAddDescriptor(formDescriptor$, fieldName, fieldIndices, fieldRecord), partition(metaAsync));
        const syncSub = syncUpdate.subscribe(dispatch);
        const asyncSub = asyncUpdate.pipe(observeOn(asyncScheduler)).subscribe(dispatch);
        return ()=>{
            syncSub.unsubscribe();
            asyncSub.unsubscribe();
        };
    }, [
        fieldName,
        fieldIndices,
        transmission$,
        formDescriptor$,
        fieldRecord
    ]);
    return state;
};
export default useFieldTransmission;
